import { js2xml } from 'xml-js'
import { DateTime } from 'luxon'
import { default as qs } from 'qs'
import { default as pMap } from 'p-map'
import { Exception } from '@poppinss/utils'
import { default as Hooks } from '@poppinss/hooks'
import { joi } from '@nhtio/lucid-resourceful/joi'
import { NextFn } from '@poppinss/middleware/types'
import { default as string } from '@poppinss/string'
import { stripUndefinedValuesFromObject } from '../../utils'
import { default as Middleware } from '@poppinss/middleware'
import { makeOpName } from '../services/odata_metadata_service'
import { normalizeError } from '../../router_macro/utils/errors'
import { useODataContext } from '@nhtio/lucid-resourceful/odata/context'
import { ODataToLucidTranslator } from '../../odata_to_lucid_translator'
import { isAdditionalResourcefulRouteDetails } from '../../router_macro/utils/type_guards'
import {
  isObject,
  isArray,
  isStringContent,
  isResourcefulModel,
  isInstanceOf,
} from '../../type_guards'
import {
  sanitizeSelect,
  sanitizeExpand,
  santizeResponsePayload,
  sanitizeODataKeys,
} from '../utils/odata'
import {
  negotiateContent,
  negotiateMetadata,
  negotiatePreference,
  negotiateIeee754Compatible,
} from '../utils/http'
import {
  ResourcefulNumberType,
  ResourcefulIntegerType,
  ResourcefulBigintType,
  ResourcefulUnsignedIntegerType,
} from '../../data_types'
import {
  E_UNRESOLVABLE_MODEL,
  E_ROUTE_NOT_FOUND,
  E_RELATIONSHIP_NOT_FOUND_EXCEPTION,
  E_RECORD_NOT_FOUND_EXCEPTION,
  E_UNSYNCABLE_RELATIONSHIP_EXCEPTION,
  E_INVALID_RELATIONSHIP_EXCEPTION,
  E_EXPAND_NOT_IMPLEMENTED,
  E_UNSUPPORTED_DATABASE_DIALECT,
  E_UNACCEPTABLE_FORMAT,
} from '@nhtio/lucid-resourceful/errors'
import type { ElementCompact } from 'xml-js'
import type { HttpContext } from '@adonisjs/core/http'
import type { ResourcefulDataType } from '../../data_types'
import type { AnySchema } from '@nhtio/lucid-resourceful/joi'
import type { ApplicationService } from '@adonisjs/core/types'
import type { Dialect } from '../../odata_to_lucid_translator'
import type { ResourcefulModelMetaSchema } from '@nhtio/lucid-resourceful/types'
import type { ResourcefulRouterModelServiceOptionsService } from '../../router_macro/services/resourceful_router_model_options_service'
import type {
  ResourcefulModel,
  ResourcefulIndexResponse,
  ResourceResourcefulRecordResponse,
  ResourcefulQueryScopeCallback,
  ResourcefulPayloadSchemaGetter,
  ResourcefulPolicy,
  ResourcefulIndexResponseHook,
  ResourceResourcefulRecordResponseHook,
  ResourcefulRouterMutatorOptions,
  RouteCallback,
  AdditionalResourcefulRouteDetails,
} from '@nhtio/lucid-resourceful/types'

type ODataMetadata = {
  context: string
} & Partial<{
  count: number
  nextLink: string
  deltaLink: string
  id: string | number | bigint
  etag: string
  readLink: string
  editLink: string
  navigationLink: string
  type: string
  mediaReadLink: string
  mediaEditLink: string
  mediaContentType: string
  mediaEtag: string
}>

const encoder = (_key: string, value: any) => {
  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toISO()
  }
  if (value instanceof DateTime) {
    return value.toISO()
  }
  // Handle Luxon DateTime values that may come from a different module instance.
  if (
    value &&
    typeof value === 'object' &&
    value.isLuxonDateTime === true &&
    typeof value.toISO === 'function'
  ) {
    return value.toISO()
  }
  if (typeof value === 'bigint') {
    return value.toString()
  }
  if (typeof value === 'number') {
    // OData JSON §7.1: Infinity and NaN are represented as string literals
    // "INF", "-INF", "NaN". Normal numbers remain numeric (not stringified).
    // NOTE: This is a behavior change from earlier versions which stringified all numbers.
    if (Number.POSITIVE_INFINITY === value) {
      return 'INF'
    }
    if (Number.NEGATIVE_INFINITY === value) {
      return '-INF'
    }
    if (Number.isNaN(value)) {
      return 'NaN'
    }
    return value
  }
  return value
}

const asXmlEntityObject = (obj: any): any => {
  obj = sanitizeODataKeys(obj)
  if (!isObject(obj)) {
    return obj
  }
  return Object.assign(
    {},
    ...Object.entries(obj).map(([key, value]) => {
      value = encoder(key, value)
      if (isObject(value)) {
        return { [`data:${key}`]: asXmlEntityObject(value) }
      } else if (isArray(value)) {
        return { [`data:${key}`]: value.map((v) => asXmlEntityObject(v)) }
      }
      return { [`data:${key}`]: { _text: String(value) } }
    })
  )
}

const elementCompactFromResourcefulModelRecord = (
  record: ResourceResourcefulRecordResponse
): ElementCompact => {
  record = sanitizeODataKeys(record) as ResourceResourcefulRecordResponse
  const ret: ElementCompact = {
    'metadata:properties': asXmlEntityObject(record),
  }
  return ret
}

const toJsonEntity = (entity: any) => {
  if (entity && typeof entity.toJSON === 'function') {
    try {
      return sanitizeODataKeys(entity.toJSON())
    } catch {
      return sanitizeODataKeys({ ...entity })
    }
  }
  return sanitizeODataKeys(entity)
}

/**
 * Determines whether a resourceful data type maps to an EDM scalar type that
 * must be serialized as a JSON string when `IEEE754Compatible=true` is
 * negotiated (per OData JSON Format §3.2 — `Edm.Int64`, `Edm.Decimal`).
 *
 * The Int64-vs-Int32 threshold mirrors
 * `ODataMetadataService#getEdmIntScalarType` so a property reported as
 * `Edm.Int64` in `$metadata` is also stringified on the wire.
 */
const isIeee754CompatibleRelevantType = (type: ResourcefulDataType): boolean => {
  if (isInstanceOf(type, 'ResourcefulBigintType', ResourcefulBigintType)) {
    return true
  }
  if (isInstanceOf(type, 'ResourcefulNumberType', ResourcefulNumberType)) {
    return true
  }
  const isInteger = isInstanceOf(type, 'ResourcefulIntegerType', ResourcefulIntegerType)
  const isUnsigned = isInstanceOf(
    type,
    'ResourcefulUnsignedIntegerType',
    ResourcefulUnsignedIntegerType
  )
  if (!isInteger && !isUnsigned) {
    return false
  }
  const intType = type as ResourcefulIntegerType | ResourcefulUnsignedIntegerType
  const min = isUnsigned ? 0 : intType.exclusiveMinimum ? intType.minimum + 1 : intType.minimum
  const max = intType.exclusiveMaximum
    ? intType.maximum
    : typeof intType.maximum === 'bigint'
      ? (intType.maximum as unknown as bigint) - BigInt(1)
      : (intType.maximum as number) - 1

  return min < -2_147_483_648 || (max as number) > 2_147_483_647
}

/** Per-request cache of resourceful meta schemas, keyed by the model class. */
type ResourcefulModelMetaCache = Map<ResourcefulModel, ResourcefulModelMetaSchema>

/**
 * Pre-warms a per-request cache of resourceful meta schemas for `rootModel`
 * and every related model reachable through its relationships. Having it
 * ready up-front keeps response serialization synchronous — the coercion
 * pass needs a related model's meta to walk into `$expand`ed nested data,
 * and `$getAsResourcefulForContext` is async.
 */
const buildModelMetaCache = async (
  rootModel: ResourcefulModel,
  ctx: HttpContext,
  app: ApplicationService
): Promise<ResourcefulModelMetaCache> => {
  const cache: ResourcefulModelMetaCache = new Map()

  const visit = async (model: ResourcefulModel): Promise<void> => {
    if (cache.has(model)) return
    let meta: ResourcefulModelMetaSchema
    try {
      meta = await model.$getAsResourcefulForContext(ctx, app)
    } catch {
      return
    }

    cache.set(model, meta)

    for (const prop of Object.values(meta.properties)) {
      if (prop.kind !== 'relationship') continue
      try {
        const related = prop.relatedModel()
        if (isResourcefulModel(related)) {
          await visit(related)
        }
      } catch {
        // Relation resolution may fail for unregistered models — safe to skip.
      }
    }
  }
  await visit(rootModel)
  return cache
}

/**
 * Coerces a single Int64/Decimal field in-place to match the negotiated
 * `IEEE754Compatible` parameter: stringifies the value when the client asked
 * for strings, or unstringifies it back to a JSON number when the client is
 * using the default JSON representation.
 *
 * NOTE: values above `Number.MAX_SAFE_INTEGER` (2^53 − 1) still get coerced
 * to `Number`, which may silently lose precision. This is an accepted
 * trade-off: `IEEE754Compatible=false` clients (Power BI in particular)
 * cannot parse string values at all — a slightly imprecise number is
 * strictly better than a hard error that blocks the entire table.
 */
const coerceNumericField = (
  entity: Record<string, unknown>,
  jsonKey: string,
  ieee754Compatible: boolean
): void => {
  const value = entity[jsonKey]
  // Null/undefined are valid JSON and carry no precision concern — leave as-is.
  if (value === null || value === undefined) return

  // Direction fork: stringify (true) vs. unstringify (false).
  if (ieee754Compatible) {
    // Already a string — matches the IEEE754=true wire format, no work needed.
    if (typeof value === 'string') return
    if (typeof value === 'bigint') {
      entity[jsonKey] = value.toString()
      return
    }
    // Non-finite numbers (Infinity/NaN) are handled by the JSON encoder per
    // OData §7.1, so skip them here instead of emitting "Infinity" strings.
    if (typeof value === 'number' && Number.isFinite(value)) {
      entity[jsonKey] = String(value)
    }
    return
  }

  // IEEE754=false path: coerce back to JSON number. bigint came from the JS
  // runtime; string came from node-postgres preserving precision on
  // bigint/numeric columns — both need unwrapping for clients that expect
  // Edm.Int64 as a number (Power BI's default).
  if (typeof value === 'bigint') {
    entity[jsonKey] = Number(value)
    return
  }
  if (typeof value !== 'string') return
  const num = Number(value)
  // Reject non-numeric strings (malformed data) rather than writing NaN.
  if (Number.isNaN(num) || !Number.isFinite(num)) return
  entity[jsonKey] = num
}

/**
 * Walks `entity` using `model`'s resourceful metadata as the guide: any
 * scalar column whose declared type maps to Edm.Int64 / Edm.Decimal is
 * coerced per the negotiated IEEE754Compatible parameter, and any relation
 * key actually present in the data (e.g. from `$expand`) is recursed into
 * using the related model's own metadata.
 */
const coerceEntityForIeee754 = (
  entity: Record<string, unknown>,
  model: ResourcefulModel,
  ieee754Compatible: boolean,
  metaCache: ResourcefulModelMetaCache,
  visited: Set<ResourcefulModel> = new Set()
): void => {
  if (visited.has(model)) return
  const meta = metaCache.get(model)
  // Meta cache miss — model wasn't reachable from the root at warm-up time.
  // Rare but possible if a hook attached records from an unrelated model.
  if (!meta) return
  const nextVisited = new Set(visited)
  nextVisited.add(model)

  for (const [propertyKey, prop] of Object.entries(meta.properties)) {
    // Fork by property kind: scalar (column / computed) vs. relation.
    if (prop.kind === 'column' || prop.kind === 'computedAccessor') {
      const serializeAs = (prop.lucidDefinitions as { serializeAs?: string | null } | undefined)
        ?.serializeAs
      // serializeAs: null means Lucid hides this column from toJSON() — it
      // won't appear in the response, so there's nothing to coerce.
      if (serializeAs === null) continue
      const jsonKey = serializeAs || propertyKey
      // Column may be declared on the model but dropped from this specific
      // response (e.g. $select, visibility policy) — skip if absent.
      if (!(jsonKey in entity)) continue
      // Only Edm.Int64 / Edm.Decimal can exceed IEEE 754 safe range. Smaller
      // integer types and booleans ride JSON numbers without precision risk,
      // so the IEEE754Compatible parameter doesn't change their wire form.
      if (!isIeee754CompatibleRelevantType(prop.definition.type)) continue
      coerceNumericField(entity, jsonKey, ieee754Compatible)
      continue
    }
    if (prop.kind !== 'relationship') continue
    // Relation absent from the data means the client didn't $expand it —
    // no nested records to walk, nothing to coerce.
    if (!(propertyKey in entity)) continue
    const nested = entity[propertyKey]
    if (nested === null || nested === undefined) continue
    let relatedModel: ResourcefulModel | null = null
    try {
      const related = prop.relatedModel()
      if (isResourcefulModel(related)) relatedModel = related
    } catch {
      // Relation resolution may fail for unregistered models — safe to skip.
    }
    if (!relatedModel) continue
    // Array vs. single object reflects the relation cardinality (hasMany /
    // manyToMany produce arrays; hasOne / belongsTo produce single objects).
    // Recurse with the related model's meta so each nested level is coerced
    // against its own column definitions.
    if (Array.isArray(nested)) {
      for (const item of nested) {
        if (item && typeof item === 'object') {
          coerceEntityForIeee754(
            item as Record<string, unknown>,
            relatedModel,
            ieee754Compatible,
            metaCache,
            nextVisited
          )
        }
      }
      continue
    }
    if (typeof nested === 'object') {
      coerceEntityForIeee754(
        nested as Record<string, unknown>,
        relatedModel,
        ieee754Compatible,
        metaCache,
        nextVisited
      )
    }
  }
}

/**
 * Shapes `respond()` can be asked to serialize. Entity bodies are plain
 * dictionaries (after `toJSON()`); collections are arrays of those. Primitive
 * bodies cover `$count` and other raw text responses; `undefined` covers
 * 204/304 and minimal-preference updates.
 */
type RespondEntityBody = Record<string, unknown>
type RespondBody = undefined | number | RespondEntityBody | RespondEntityBody[]

/**
 * Arguments for `respond()`.
 *
 * @property status HTTP status code of the response.
 * @property body Payload to serialize — a single entity, a collection of
 *   entities, a primitive (e.g. `$count`), or `undefined` for empty bodies
 *   (204/304, minimal-preference updates).
 * @property metadata OData `@odata.*` annotations for the response envelope
 *   (context, count, nextLink, id, etag, etc.).
 * @property model Optional. The resourceful model describing the entity
 *   shape of `body`. Drives the IEEE754 coercion pass — Edm.Int64 /
 *   Edm.Decimal detection and `$expand` recursion into related models. Omit
 *   when `body` isn't entity-shaped — 204/304 responses, error envelopes,
 *   raw `$count` numbers — since there's nothing for the coercion pass to
 *   walk.
 * @property isArray Optional. Marks `body` as a collection so it's serialized
 *   into an OData feed (`value: [...]` / `<feed>`) instead of a single
 *   entry. Omit when there's no body (204/304) or the body is a primitive —
 *   there's no ambiguity in those cases. Defaults to `Array.isArray(body)`.
 * @property headers Optional. Request-level response headers to merge on
 *   top of macro-level and controller-level defaults.
 * @property raw Optional. When `true`, serializes `String(body)` as
 *   `text/plain` and skips OData envelope construction — used for `$count`
 *   endpoints that must return a bare number per the spec.
 */
interface RespondOptions {
  status: number
  body: RespondBody
  metadata: ODataMetadata
  model?: ResourcefulModel
  isArray?: boolean
  headers?: { [key: string]: string }
  raw?: boolean
}

interface ModelRelationshipSyncPayload {
  ruids: Array<string | number>
}

const modelRelationshipSyncPayloadSchema = joi.object<ModelRelationshipSyncPayload>({
  ruids: joi
    .array()
    .items(joi.alternatives().try(joi.string().required(), joi.number().integer().required()))
    .required(),
})

export type ResourcefulModelControllerHooks = {
  index: [
    [ResourcefulIndexResponse, HttpContext],
    [error: Error | null, ResourcefulIndexResponse, HttpContext],
  ]
  create: [
    [ResourceResourcefulRecordResponse, HttpContext],
    [error: Error | null, ResourceResourcefulRecordResponse, HttpContext],
  ]
  read: [
    [ResourceResourcefulRecordResponse, HttpContext],
    [error: Error | null, ResourceResourcefulRecordResponse, HttpContext],
  ]
  readRelated: [
    [ResourcefulIndexResponse, HttpContext],
    [error: Error | null, ResourcefulIndexResponse, HttpContext],
  ]
  update: [
    [ResourceResourcefulRecordResponse, HttpContext],
    [error: Error | null, ResourceResourcefulRecordResponse, HttpContext],
  ]
}

export type ODataEntityUidAndRelationshipInformation = {
  uid: string | undefined
  relationship: string | undefined
  isCountRequest: boolean
}

type MiddlewareFn = (ctx: HttpContext, next: NextFn) => void | Promise<void>

export class ResourcefulModelController {
  readonly #subpath: string
  readonly #model: Promise<ResourcefulModel | E_UNRESOLVABLE_MODEL>
  readonly #options: ResourcefulRouterModelServiceOptionsService
  readonly #headers: {
    [key: string]: string
  }
  readonly #mutators: Hooks<ResourcefulModelControllerHooks>
  readonly #scopeRestrictors: ResourcefulQueryScopeCallback[]
  readonly #payloadRestrictors: ResourcefulPayloadSchemaGetter[]
  readonly #operations: Map<string, RouteCallback | AdditionalResourcefulRouteDetails>
  get options() {
    return this.#options
  }

  constructor(
    subpath: string,
    model: Promise<ResourcefulModel | E_UNRESOLVABLE_MODEL>,
    options: ResourcefulRouterModelServiceOptionsService,
    headers: {
      [key: string]: string
    },
    groupResourcefulQueryScopeCallbacks?: ResourcefulQueryScopeCallback[],
    groupResourcefulPayloadSchemaGetters?: ResourcefulPayloadSchemaGetter[],
    groupResourcefulRouterMutatorOptions?: ResourcefulRouterMutatorOptions,
    groupResourcefulPolicies?: ResourcefulPolicy[]
  ) {
    this.#subpath = subpath
    this.#model = model
    this.#options = options
    this.#headers = headers
    this.#mutators = new Hooks<ResourcefulModelControllerHooks>()
    this.#scopeRestrictors = [
      ...(groupResourcefulQueryScopeCallbacks || []),
      ...(this.#options.scopeRestrictors || []),
    ]
    this.#payloadRestrictors = [
      ...(groupResourcefulPayloadSchemaGetters || []),
      ...(this.#options.payloadRestrictors || []),
    ]
    if (isObject(groupResourcefulRouterMutatorOptions)) {
      if (
        'index' in groupResourcefulRouterMutatorOptions &&
        isArray(groupResourcefulRouterMutatorOptions.index)
      ) {
        groupResourcefulRouterMutatorOptions.index.forEach((hook) => {
          this.#mutators.add('index', hook as ResourcefulIndexResponseHook)
        })
      }
      if (
        'create' in groupResourcefulRouterMutatorOptions &&
        isArray(groupResourcefulRouterMutatorOptions.create)
      ) {
        groupResourcefulRouterMutatorOptions.create.forEach((hook) => {
          this.#mutators.add('create', hook as ResourceResourcefulRecordResponseHook)
        })
      }
      if (
        'read' in groupResourcefulRouterMutatorOptions &&
        isArray(groupResourcefulRouterMutatorOptions.read)
      ) {
        groupResourcefulRouterMutatorOptions.read.forEach((hook) => {
          this.#mutators.add('read', hook as ResourceResourcefulRecordResponseHook)
        })
      }
      if (
        'readRelated' in groupResourcefulRouterMutatorOptions &&
        isArray(groupResourcefulRouterMutatorOptions.readRelated)
      ) {
        groupResourcefulRouterMutatorOptions.readRelated.forEach((hook) => {
          this.#mutators.add('readRelated', hook as ResourceResourcefulRecordResponseHook)
        })
      }
      if (
        'update' in groupResourcefulRouterMutatorOptions &&
        isArray(groupResourcefulRouterMutatorOptions.update)
      ) {
        groupResourcefulRouterMutatorOptions.update.forEach((hook) => {
          this.#mutators.add('update', hook as ResourceResourcefulRecordResponseHook)
        })
      }
    }
    if (isObject(this.#options.mutators)) {
      if ('index' in this.#options.mutators && isArray(this.#options.mutators.index)) {
        this.#options.mutators.index.forEach((hook) => {
          this.#mutators.add('index', hook as ResourcefulIndexResponseHook)
        })
      }
      if ('create' in this.#options.mutators && isArray(this.#options.mutators.create)) {
        this.#options.mutators.create.forEach((hook) => {
          this.#mutators.add('create', hook as ResourceResourcefulRecordResponseHook)
        })
      }
      if ('read' in this.#options.mutators && isArray(this.#options.mutators.read)) {
        this.#options.mutators.read.forEach((hook) => {
          this.#mutators.add('read', hook as ResourceResourcefulRecordResponseHook)
        })
      }
      if ('readRelated' in this.#options.mutators && isArray(this.#options.mutators.readRelated)) {
        this.#options.mutators.readRelated.forEach((hook) => {
          this.#mutators.add('readRelated', hook as ResourceResourcefulRecordResponseHook)
        })
      }
      if ('update' in this.#options.mutators && isArray(this.#options.mutators.update)) {
        this.#options.mutators.update.forEach((hook) => {
          this.#mutators.add('update', hook as ResourceResourcefulRecordResponseHook)
        })
      }
    }
    if (groupResourcefulPolicies) {
      groupResourcefulPolicies.forEach((policy) => {
        if (policy.scope) {
          this.#scopeRestrictors.push(policy.scope)
        }
        if (policy.payload) {
          this.#payloadRestrictors.push(policy.payload)
        }
        if (isObject(policy.mutators)) {
          if ('index' in policy.mutators && isArray(policy.mutators.index)) {
            policy.mutators.index.forEach((hook) => {
              this.#mutators.add('index', hook as ResourcefulIndexResponseHook)
            })
          }
          if ('create' in policy.mutators && isArray(policy.mutators.create)) {
            policy.mutators.create.forEach((hook) => {
              this.#mutators.add('create', hook as ResourceResourcefulRecordResponseHook)
            })
          }
          if ('read' in policy.mutators && isArray(policy.mutators.read)) {
            policy.mutators.read.forEach((hook) => {
              this.#mutators.add('read', hook as ResourceResourcefulRecordResponseHook)
            })
          }
          if ('readRelated' in policy.mutators && isArray(policy.mutators.readRelated)) {
            policy.mutators.readRelated.forEach((hook) => {
              this.#mutators.add('readRelated', hook as ResourceResourcefulRecordResponseHook)
            })
          }
          if ('update' in policy.mutators && isArray(policy.mutators.update)) {
            policy.mutators.update.forEach((hook) => {
              this.#mutators.add('update', hook as ResourceResourcefulRecordResponseHook)
            })
          }
        }
      })
    }
    this.#operations = new Map<string, RouteCallback | AdditionalResourcefulRouteDetails>()
  }

  static async getResourcefulModelCreatePayloadValidationSchema(
    model: ResourcefulModel,
    ctx: HttpContext,
    app: ApplicationService
  ) {
    const schemas: AnySchema[] = []
    await pMap(
      model.$resourcefulPayloadValidationSchemaBuilders.create,
      async (builder) => {
        const schema = await builder(ctx, app)
        if (schema) {
          schemas.push(schema)
        }
      },
      { concurrency: 1 }
    )
    let ret = joi.any()
    schemas.forEach((schema) => {
      ret = ret.concat(schema)
    })
    return ret
  }

  async #getWriteValidationSchema(
    ctx: HttpContext,
    model: ResourcefulModel,
    app: ApplicationService,
    forUpdate: boolean = false
  ) {
    const schema = await model.$asOpenApiSchemaObject(ctx, app, 'write')
    const asResourceful = await model.$getAsResourcefulForContext(ctx, app)
    const modelJoiSchema =
      await ResourcefulModelController.getResourcefulModelCreatePayloadValidationSchema(
        model,
        ctx,
        app
      )
    return joi.object(
      Object.assign(
        {},
        ...Object.keys(schema.properties!)
          .filter((p) => p !== model.primaryKey)
          .map((creatablePropertyKey) => {
            const required =
              !forUpdate &&
              Array.isArray(schema.required) &&
              schema.required.includes(creatablePropertyKey)
            const returnable = {
              [creatablePropertyKey]: joi.any(),
            }
            const prop = asResourceful.properties[creatablePropertyKey]
            if (prop) {
              const validator = prop.validator
              if (validator) {
                returnable[creatablePropertyKey] = validator
              }
            }
            try {
              const extracted = modelJoiSchema.extract(creatablePropertyKey)
              if (extracted) {
                returnable[creatablePropertyKey] = returnable[creatablePropertyKey]
                  ? returnable[creatablePropertyKey].concat(extracted)
                  : extracted
              }
            } catch {
              // noop
            }
            if (required) {
              returnable[creatablePropertyKey] = returnable[creatablePropertyKey].concat(
                joi.any().required()
              )
            } else {
              returnable[creatablePropertyKey] = returnable[creatablePropertyKey].concat(
                joi.any().optional()
              )
            }
            return returnable
          })
      )
    )
  }

  async getODataContextUrl(fragment: string) {
    const odataContext = await useODataContext()
    let base = odataContext.serviceBaseUrl
    if (!base.endsWith('/')) {
      base += '/'
    }
    const parts = [base, '$metadata', '#', fragment]
    return parts.join('')
  }

  async resolveFullUrl(relative: string, ...more: string[]) {
    const odataContext = await useODataContext()
    let base = odataContext.serviceBaseUrl
    if (!base.endsWith('/')) {
      base += '/'
    }
    const parts = [base, relative, ...more]
    return parts.join('/')
  }

  async resolveBaseRequirements() {
    const [model, { default: app }] = await Promise.all([
      this.#model,
      import('@adonisjs/core/services/app'),
    ])
    return { model, app }
  }

  resolveUrlResource(ctx: HttpContext) {
    const star = ctx.params['*']
    let resource = Array.isArray(star)
      ? `/${star.join('/')}`
      : typeof star === 'string'
        ? star.startsWith('/')
          ? star
          : `/${star}`
        : '/'
    resource = resource.replace(/\/{2,}/g, '/')
    return resource
  }

  async resolveRequestRequirements(ctx: HttpContext) {
    const { model, app } = await this.resolveBaseRequirements()
    const serveAs = negotiateContent(ctx)
    const metadataLevel = negotiateMetadata(ctx)
    const preference = negotiatePreference(ctx)
    const ieee754Compatible = negotiateIeee754Compatible(ctx)
    const method = ctx.request.method() || 'GET'
    const url = ctx.request.url() || '/'
    if (model instanceof E_UNRESOLVABLE_MODEL) {
      throw new E_ROUTE_NOT_FOUND([method, url])
    }
    // Pre-warm a per-request cache of resourceful meta schemas for the root
    // model and every reachable related model. The respond() coercion pass
    // walks the data guided by each entity's own model meta (for Edm.Int64 /
    // Edm.Decimal detection and $expand recursion), and this keeps that walk
    // synchronous.
    let metaCache: ResourcefulModelMetaCache = new Map()
    if (isResourcefulModel(model)) {
      try {
        metaCache = await buildModelMetaCache(model, ctx, app)
      } catch {
        metaCache = new Map()
      }
    }

    const respond = (options: RespondOptions) => {
      const {
        status,
        body,
        metadata,
        model: coercionModel,
        isArray: isCollection,
        headers = {},
        raw = false,
      } = options

      ctx.response.status(status)
      // apply macro-level headers
      Object.entries(this.#headers).forEach(([key, value]) => ctx.response.header(key, value))
      // apply controller-level headers
      Object.entries(this.#options.headers).forEach(([key, value]) =>
        ctx.response.header(key, value)
      )
      // apply request-level headers
      Object.entries(headers).forEach(([key, value]) => ctx.response.header(key, value))
      // apply OData headers
      if (!ctx.response.getHeader('OData-Version')) {
        ctx.response.header('OData-Version', '4.0')
      }
      if (metadata.etag) {
        ctx.response.header('ETag', metadata.etag)
      }
      // Handle empty/no-content responses
      if (status === 204 || status === 304 || body === null || typeof body === 'undefined') {
        ctx.response.send(null)
        return
      }
      if (raw) {
        ctx.response.header('Content-Type', `text/plain`)
        ctx.response.send(String(body))
        return
      }
      // apply content-type headers
      switch (serveAs) {
        case 'xml': {
          const typeParam = isCollection ? 'feed' : 'entry'
          ctx.response.header(
            'Content-Type',
            `application/atom+xml;type=${typeParam};odata.metadata=${metadataLevel}`
          )
          break
        }
        default: {
          // Per OData JSON §3.2, the server MUST NOT advertise a
          // `IEEE754Compatible` parameter it does not actually honor in the
          // payload. Emitting `=true` while writing numeric `Edm.Int64` /
          // `Edm.Decimal` values is what triggers Power BI's
          // `DataSource.Error: Cannot convert a value to target type
          // 'Edm.Int64' because of conflict between input format
          // string/number and parameter 'IEEE754Compatible' false/true`.
          // We advertise the parameter that matches the value we're actually
          // going to serialize, based on the client's negotiation.
          ctx.response.header(
            'Content-Type',
            `application/json;odata.metadata=${metadataLevel};IEEE754Compatible=${
              ieee754Compatible ? 'true' : 'false'
            }`
          )
          break
        }
      }

      const include = (name: keyof ODataMetadata) => {
        if (serveAs === 'xml') return true
        if (metadataLevel === 'none') return false
        if (metadataLevel === 'minimal') {
          return ['context', 'count', 'nextLink', 'deltaLink', 'id', 'etag'].includes(name)
        }
        return true // full
      }

      // IEEE754 coercion helper — walks the JSON entity using the target
      // model's meta to decide which fields need stringifying/unstringifying,
      // and recurses into $expand-ed relations using their own model meta.
      const applyIeee754Coercion = (jsonEntity: unknown): void => {
        if (!coercionModel) return
        if (!jsonEntity || typeof jsonEntity !== 'object' || Array.isArray(jsonEntity)) return

        coerceEntityForIeee754(
          jsonEntity as Record<string, unknown>,
          coercionModel,
          ieee754Compatible,
          metaCache
        )
      }

      // JSON
      if (serveAs !== 'xml') {
        if (isCollection) {
          const records = body as RespondEntityBody[]
          const value = records.map((item) => {
            const jsonEntity = toJsonEntity(item)
            applyIeee754Coercion(jsonEntity)
            return jsonEntity
          })
          const payload: Record<string, any> = stripUndefinedValuesFromObject({
            // Annotations
            ['@odata.context']: include('context') ? metadata.context : undefined,
            ['@odata.count']: include('count') ? metadata.count : undefined,
            ['@odata.nextLink']: include('nextLink') ? metadata.nextLink : undefined,
            ['@odata.deltaLink']: include('deltaLink') ? metadata.deltaLink : undefined,
            // Data
            value,
          })
          ctx.response.send(JSON.stringify(payload, encoder))
          return
        }

        // Single entity or primitive
        const entity = toJsonEntity(body)
        if (isObject(entity)) {
          applyIeee754Coercion(entity)
          const payload: Record<string, any> = stripUndefinedValuesFromObject({
            ['@odata.context']: include('context') ? metadata.context : undefined,
            ['@odata.id']:
              include('id') && metadata.id !== undefined ? String(metadata.id) : undefined,
            ['@odata.etag']: include('etag') ? metadata.etag : undefined,
            ['@odata.readLink']: include('readLink') ? metadata.readLink : undefined,
            ['@odata.editLink']: include('editLink') ? metadata.editLink : undefined,
            ['@odata.type']: include('type') ? metadata.type : undefined,
            ['@odata.mediaReadLink']: include('mediaReadLink') ? metadata.mediaReadLink : undefined,
            ['@odata.mediaEditLink']: include('mediaEditLink') ? metadata.mediaEditLink : undefined,
            ['@odata.mediaContentType']: include('mediaContentType')
              ? metadata.mediaContentType
              : undefined,
            ['@odata.mediaEtag']: include('mediaEtag') ? metadata.mediaEtag : undefined,
            ...entity,
          })
          ctx.response.send(JSON.stringify(payload, encoder))
          return
        } else {
          // Primitive value
          const payload: Record<string, any> = stripUndefinedValuesFromObject({
            ['@odata.context']: include('context') ? metadata.context : undefined,
            value: entity,
          })
          ctx.response.send(JSON.stringify(payload, encoder))
          return
        }
      }

      // XML (Atom)
      const atomNs = 'http://www.w3.org/2005/Atom'
      const mNs = 'http://docs.oasis-open.org/odata/ns/metadata'
      const dNs = 'http://docs.oasis-open.org/odata/ns/data'

      const buildEntry = (entity: any, meta: ODataMetadata): ElementCompact => {
        const obj = toJsonEntity(entity) as any
        const entryAttrs: Record<string, any> = stripUndefinedValuesFromObject({
          'metadata:context': include('context') ? meta.context : undefined,
          'metadata:etag': include('etag') ? meta.etag : undefined,
        })

        const entryNode: ElementCompact = {
          entry: {
            _attributes: {
              'xmlns': atomNs,
              'xmlns:metadata': mNs,
              'xmlns:data': dNs,
              ...entryAttrs,
            },
            // Optional Atom children (gated by metadata level)
            ...(include('id') && meta.id !== undefined ? { id: { _text: String(meta.id) } } : {}),
            ...(include('readLink') && meta.readLink
              ? { link: [{ _attributes: { rel: 'self', href: meta.readLink } }] }
              : {}),
            ...(include('editLink') && meta.editLink
              ? {
                  link: [
                    ...(meta.readLink
                      ? [{ _attributes: { rel: 'self', href: meta.readLink } }]
                      : []),
                    { _attributes: { rel: 'edit', href: meta.editLink } },
                  ],
                }
              : {}),
            content: {
              _attributes: { type: 'application/xml' },
              ...elementCompactFromResourcefulModelRecord(obj),
            },
          },
        }
        return entryNode.entry as ElementCompact
      }

      if (isCollection) {
        const feedAttrs: Record<string, any> = stripUndefinedValuesFromObject({
          'metadata:context': include('context') ? metadata.context : undefined,
        })

        const linkNext =
          include('nextLink') && metadata.nextLink
            ? [
                {
                  _attributes: { rel: 'next', href: metadata.nextLink },
                },
              ]
            : undefined

        const feedDoc: ElementCompact = {
          _declaration: { _attributes: { version: '1.0', encoding: 'utf-8' } },
          feed: stripUndefinedValuesFromObject({
            _attributes: {
              'xmlns': atomNs,
              'xmlns:metadata': mNs,
              'xmlns:data': dNs,
              ...feedAttrs,
            },
            ...(include('count') && typeof metadata.count === 'number'
              ? { 'metadata:count': { _text: String(metadata.count) } }
              : {}),
            ...(linkNext ? { link: linkNext } : {}),
            entry: (body as RespondEntityBody[]).map((item) => buildEntry(item, metadata)),
          }) as any,
        }

        const xml = js2xml(feedDoc, { compact: true })
        ctx.response.send(xml)
        return
      }

      const entryDoc: ElementCompact = {
        _declaration: { _attributes: { version: '1.0', encoding: 'utf-8' } },
        ...(() => {
          const entry = buildEntry(body, metadata)
          return { entry }
        })(),
      }
      const xml = js2xml(entryDoc, { compact: true })
      ctx.response.send(xml)
    }
    const query = ctx.request.qs()

    const requestedFormat =
      typeof query['$format'] === 'string' ? query['$format'].trim() : undefined
    if (requestedFormat && !requestedFormat.includes('json') && !requestedFormat.includes('xml')) {
      throw new E_UNACCEPTABLE_FORMAT(requestedFormat)
    }

    // Validate Accept header per OData §7. Allow standard JSON/XML types plus
    // vendor/structured suffixes (e.g. application/vnd.odata+json, application/*+json)
    // to avoid rejecting legitimate non-Power-BI clients.
    const accept = String(ctx.request.header('accept') || '').toLowerCase()
    if (
      accept &&
      !accept.includes('*/*') &&
      !accept.includes('application/json') &&
      !accept.includes('application/xml') &&
      !accept.includes('text/xml') &&
      !accept.includes('+json') &&
      !accept.includes('+xml')
    ) {
      throw new E_UNACCEPTABLE_FORMAT(accept)
    }

    const allowedSystemOptions = new Set([
      '$top',
      '$skip',
      '$count',
      '$orderby',
      '$select',
      '$filter',
      '$expand',
      '$format',
      '$search',
    ])
    for (const key of Object.keys(query)) {
      if (key.startsWith('$') && !allowedSystemOptions.has(key)) {
        throw new Exception(`Unsupported OData system query option: ${key}`, {
          status: 400,
          code: 'E_ODATA_UNSUPPORTED_SYSTEM_QUERY_OPTION',
        })
      }
    }

    const { valid: rawSelections, isFullSet } = await sanitizeSelect(query, model, ctx, app)
    const selections = Array.isArray(rawSelections) ? [...rawSelections] : rawSelections
    const expansions = await sanitizeExpand(query)
    if (Array.isArray(expansions) && expansions.length > 0) {
      const readSchema = await model.$asOpenApiSchemaObject(ctx, app, 'read')
      const possible =
        !isObject(readSchema) || !('properties' in readSchema) || !isObject(readSchema.properties)
          ? []
          : Object.keys(readSchema.properties)
      const possibleMap = new Map(possible.map((k) => [k.toLowerCase(), k]))
      const relationshipMap = new Map(
        Array.from(model.$resourcefulRelationships.keys()).map((k) => [k.toLowerCase(), k])
      )

      const unsupported: string[] = []
      const resolvedExpansions: string[] = []
      for (const expansion of expansions) {
        const lcExpansion = expansion.toLowerCase()
        const resolved = relationshipMap.get(lcExpansion) || possibleMap.get(lcExpansion)
        if (!resolved) {
          unsupported.push(expansion)
          continue
        }
        resolvedExpansions.push(resolved)
      }
      if (unsupported.length > 0) {
        throw new E_EXPAND_NOT_IMPLEMENTED(model.$resourcefulName, unsupported)
      }
      if (Array.isArray(selections)) {
        for (const expansion of expansions) {
          if (!selections.includes(expansion)) {
            selections.push(expansion)
          }
        }
        for (const expansion of resolvedExpansions) {
          if (!selections.includes(expansion)) {
            selections.push(expansion)
          }
        }
      }
    }
    return {
      model,
      app,
      prefix: this.#subpath,
      serveAs,
      method,
      url,
      resource: this.resolveUrlResource(ctx),
      query,
      selections,
      isFullSet,
      metadataLevel,
      preference,
      respond,
    }
  }

  #parseInstanceUidFromResourceFragment(resource: string) {
    const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    // normalize subpath to start with a single "/" and have no trailing "/"
    const sub = (() => {
      let s = this.#subpath || ''
      s = s.replace(/\/{2,}/g, '/').replace(/\/+$/, '')
      if (!s.startsWith('/')) s = `/${s}`
      return s
    })()
    // match: ^/<subpath>(<key>) then end or next segment
    const pattern = new RegExp(`^${esc(sub)}\\(([^)]*)\\)(?:$|\\/)`)
    const match = resource.match(pattern)
    if (match && match[1] !== undefined) {
      let v = match[1]
      if ((v.startsWith("'") && v.endsWith("'")) || (v.startsWith('"') && v.endsWith('"'))) {
        v = v.slice(1, -1)
        if (v.includes("''")) v = v.replace(/''/g, "'")
      }
      return v
    }
    return undefined
  }

  #parseInstanceUidAndRelationshipNameFromResourceFragment(
    resource: string
  ): ODataEntityUidAndRelationshipInformation {
    const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    // normalize subpath to start with a single "/" and have no trailing "/"
    const sub = (() => {
      let s = this.#subpath || ''
      s = s.replace(/\/{2,}/g, '/').replace(/\/+$/, '')
      if (!s.startsWith('/')) s = `/${s}`
      return s
    })()

    // match: ^/<subpath>(<key>)/<relationship> with optional /$count
    const pattern = new RegExp(`^${esc(sub)}\\(([^)]*)\\)\\/([^\\/]+)(?:\\/\\$count)?$`)
    const match = resource.match(pattern)

    if (match && match[1] !== undefined && match[2] !== undefined) {
      // Extract and clean the UID
      let uid = match[1]
      if (
        (uid.startsWith("'") && uid.endsWith("'")) ||
        (uid.startsWith('"') && uid.endsWith('"'))
      ) {
        uid = uid.slice(1, -1)
        if (uid.includes("''")) uid = uid.replace(/''/g, "'")
      }

      // Extract relationship name
      const relationship = match[2]

      // Check if it's a count request
      const isCountRequest = resource.endsWith('/$count')

      return {
        uid,
        relationship,
        isCountRequest,
      }
    }

    // Return undefined values if pattern doesn't match
    return {
      uid: undefined,
      relationship: undefined,
      isCountRequest: false,
    }
  }

  #parseODataMetadataIdFromResourceFragment(resource: string) {
    while (resource.startsWith('/')) {
      resource = resource.slice(1)
    }
    return resource
  }

  #makeSelectionSubsetForContextUrl(
    selections: string[] | undefined,
    isFullSet: boolean,
    model: ResourcefulModel
  ): string {
    if (isFullSet) {
      return ''
    }
    const props = [model.primaryKey]
    if (Array.isArray(selections)) {
      props.push(...selections.filter((s) => s !== model.primaryKey))
    }
    return `(${props.join(',')})`
  }

  #entityEtag(record: any): string | undefined {
    if (!record || typeof record !== 'object') {
      return undefined
    }
    const basis = record.updatedAt || record.createdAt || record.id
    if (!basis) {
      return undefined
    }
    return `W/"${String(basis)}"`
  }

  #normalizeEtag(value: string): string {
    return value.trim().replace(/^W\//i, '')
  }

  async #serveModelRead(ctx: HttpContext) {
    console.log('ResourcefulModelController##serveModelRead')
    const { resource, model, app, respond, selections, preference } =
      await this.resolveRequestRequirements(ctx)
    const uid = this.#parseInstanceUidFromResourceFragment(resource)
    if (!uid) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    if (!uid || !/^-?\d+$/.test(uid)) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const id = Number(uid)
    if (!Number.isSafeInteger(id)) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const ret: ResourceResourcefulRecordResponse<typeof model> = await model.$onResourcefulRead(
      id,
      ctx,
      app,
      this.#scopeRestrictors
    )
    const runner = this.#mutators.runner('read')
    await runner.run(ret, ctx)
    const metadataId = this.#parseODataMetadataIdFromResourceFragment(resource)
    const etag = this.#entityEtag(ret)

    const ifNoneMatch = ctx.request.header('if-none-match')
    if (etag && typeof ifNoneMatch === 'string') {
      const values = ifNoneMatch
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0)
      const current = this.#normalizeEtag(etag)
      const matched = values.some((v) => v === '*' || this.#normalizeEtag(v) === current)
      if (matched) {
        return respond({
          status: 304,
          body: undefined,
          metadata: {
            context: await this.getODataContextUrl(`${this.#subpath}/$entity`),
            etag,
          },
        })
      }
    }

    const metadata: ODataMetadata = {
      context: await this.getODataContextUrl(
        `${this.#subpath}${this.#makeSelectionSubsetForContextUrl(selections, 'undefined' === typeof selections, model)}/$entity`
      ),
      id: await this.resolveFullUrl(metadataId),
      readLink: metadataId,
      editLink: metadataId,
      etag,
    }
    return respond({
      status: 200,
      body: santizeResponsePayload(ret, selections, preference),
      metadata,
      model,
      isArray: false,
    })
  }

  async #serveModelUpdate(ctx: HttpContext) {
    const { resource, model, app, respond, selections, preference } =
      await this.resolveRequestRequirements(ctx)
    const uid = this.#parseInstanceUidFromResourceFragment(resource)
    if (!uid) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    if (!uid || !/^-?\d+$/.test(uid)) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const id = Number(uid)
    if (!Number.isSafeInteger(id)) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const schema = await this.#getWriteValidationSchema(ctx, model, app, true)
    const { error, value: validated } = schema.validate(
      stripUndefinedValuesFromObject({
        ...ctx.request.all(),
        [model.primaryKey]: undefined,
      }),
      {
        abortEarly: false,
        convert: true,
      }
    )
    if (error) {
      throw normalizeError(error)
    }
    try {
      const existing = await model.$onResourcefulRead(id, ctx, app, this.#scopeRestrictors)
      const existingEtag = this.#entityEtag(existing)
      const ifMatch = ctx.request.header('if-match')
      if (existingEtag && typeof ifMatch === 'string' && ifMatch.trim() !== '*') {
        const requested = ifMatch
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v.length > 0)
        const current = this.#normalizeEtag(existingEtag)
        const matched = requested.some((v) => this.#normalizeEtag(v) === current)
        if (!matched) {
          return respond({
            status: 412,
            body: {
              error: {
                code: 'E_PRECONDITION_FAILED',
                message: 'If-Match precondition failed',
              },
            },
            metadata: {
              context: await this.getODataContextUrl(`${this.#subpath}/$entity`),
              etag: existingEtag,
            },
          })
        }
      }

      const ret: ResourceResourcefulRecordResponse<typeof model> = await model.$onResourcefulUpdate(
        id,
        validated,
        ctx,
        app,
        {
          queryScopeCallbacks: this.#scopeRestrictors,
          payloadValidationSchemas: this.#payloadRestrictors,
        }
      )
      const runner = this.#mutators.runner('update')
      await runner.run(ret, ctx)
      const metadataId = this.#parseODataMetadataIdFromResourceFragment(resource)
      const metadata: ODataMetadata = {
        context: await this.getODataContextUrl(
          `${this.#subpath}${this.#makeSelectionSubsetForContextUrl(selections, 'undefined' === typeof selections, model)}/$entity`
        ),
        id: await this.resolveFullUrl(metadataId),
        readLink: metadataId,
        editLink: metadataId,
        etag: this.#entityEtag(ret),
      }
      if (preference === 'minimal') {
        return respond({
          status: 204,
          body: undefined,
          metadata,
          headers: { 'Preference-Applied': 'return=minimal' },
        })
      }
      return respond({
        status: 200,
        body: santizeResponsePayload(ret, selections, preference),
        metadata,
        model,
        isArray: false,
        headers: { 'Preference-Applied': 'return=representation' },
      })
    } catch (e) {
      throw normalizeError(e)
    }
  }

  async #serveModelDelete(ctx: HttpContext) {
    const { resource, model, app, respond } = await this.resolveRequestRequirements(ctx)
    const uid = this.#parseInstanceUidFromResourceFragment(resource)
    if (!uid) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    if (!uid || !/^-?\d+$/.test(uid)) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const id = Number(uid)
    if (!Number.isSafeInteger(id)) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    try {
      await model.$onResourcefulDelete(id, ctx, app, this.#scopeRestrictors)
      return respond({
        status: 204,
        body: undefined,
        metadata: {
          context: await this.getODataContextUrl(`${this.#subpath}/$entity`),
        },
      })
    } catch (e) {
      throw normalizeError(e)
    }
  }

  async #serveModelCreate(ctx: HttpContext) {
    const { model, app, respond, preference, selections } =
      await this.resolveRequestRequirements(ctx)
    const schema = await this.#getWriteValidationSchema(ctx, model, app)
    const { error, value: validated } = schema.validate(ctx.request.all(), {
      abortEarly: false,
      convert: true,
    })
    if (error) {
      throw normalizeError(error)
    }
    try {
      const ret: any = await model.$onResourcefulCreate(
        validated,
        ctx,
        app,
        this.#payloadRestrictors
      )
      const runner = this.#mutators.runner('create')
      await runner.run(ret, ctx)
      const resource = `/${this.#subpath}(${encodeURIComponent(String(ret[model.primaryKey]))})`
      const metadataId = this.#parseODataMetadataIdFromResourceFragment(resource)
      const metadata: ODataMetadata = {
        context: await this.getODataContextUrl(`${this.#subpath}/$entity`),
        id: await this.resolveFullUrl(metadataId),
        readLink: metadataId,
        editLink: metadataId,
      }
      return respond({
        status: 201,
        body: santizeResponsePayload(ret, selections, preference),
        metadata,
        model,
        isArray: false,
        headers: { Location: String(metadata.id!) },
      })
    } catch (e) {
      throw normalizeError(e)
    }
  }

  async #serveModelReadRelated(ctx: HttpContext) {
    const { resource, model, app, respond, preference, query } =
      await this.resolveRequestRequirements(ctx)

    let { uid, relationship, isCountRequest } =
      this.#parseInstanceUidAndRelationshipNameFromResourceFragment(resource)

    // Fallback parser for property raw-value endpoints: /EntitySet(id)/property/$value
    // Supports numeric ids, GUIDs, and single-quoted string keys per OData URL conventions.
    if (!uid || !relationship) {
      const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const sub = (() => {
        let s = this.#subpath || ''
        s = s.replace(/\/{2,}/g, '/').replace(/\/+$/, '')
        if (!s.startsWith('/')) s = `/${s}`
        return s
      })()
      // Match keys in parentheses: numeric (123), GUID (xxx-xxx), or single-quoted strings ('foo')
      const valueMatch = resource.match(
        new RegExp(`^${esc(sub)}\\(([^)]*(?:'[^']*'[^)]*)?)\\)\\/([^\\/]+)\\/\\$value$`)
      )
      if (valueMatch && valueMatch[1] && valueMatch[2]) {
        uid = valueMatch[1]
        relationship = valueMatch[2]
        isCountRequest = false
      }
    }

    if (!uid) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }

    // Resolve uid: strip single-quotes for string keys, parse numeric keys.
    // Supports numeric ids, GUIDs, and single-quoted string keys per OData URL conventions.
    let resolvedId: string | number
    const strippedUid = uid.startsWith("'") && uid.endsWith("'") ? uid.slice(1, -1) : uid
    if (/^-?\d+$/.test(strippedUid)) {
      const numId = Number(strippedUid)
      if (!Number.isSafeInteger(numId)) {
        throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
      }
      resolvedId = numId
    } else if (strippedUid.length > 0) {
      // String or GUID key — pass through as-is
      resolvedId = strippedUid
    } else {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }

    if (!relationship) {
      throw new E_RELATIONSHIP_NOT_FOUND_EXCEPTION()
    }

    // Support primitive property raw value requests such as /Users(1)/username/$value.
    const valueSuffix = `${relationship}/$value`
    if (
      resource.endsWith(`/${valueSuffix}`) &&
      !model.$resourcefulRelationships.has(relationship)
    ) {
      let record: Record<string, unknown>
      try {
        record = (await model.$onResourcefulRead(
          resolvedId as any,
          ctx,
          app,
          this.#scopeRestrictors
        )) as Record<string, unknown>
      } catch {
        // Key type mismatch or not found — surface as 404, not 500.
        throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
      }
      if (!(relationship in record)) {
        throw new E_RELATIONSHIP_NOT_FOUND_EXCEPTION()
      }
      const value = record[relationship]
      ctx.response.header('Content-Type', 'text/plain; charset=utf-8')
      return ctx.response.status(200).send(value === null ? '' : String(value))
    }

    const resourcefulRelationshipDefinition = model.$resourcefulRelationships.get(relationship)
    if (!resourcefulRelationshipDefinition) {
      throw new E_RELATIONSHIP_NOT_FOUND_EXCEPTION()
    }
    const lucidRelationshipDefinition = model.$getRelation(relationship)
    if (!lucidRelationshipDefinition) {
      throw new E_RELATIONSHIP_NOT_FOUND_EXCEPTION()
    }
    const relatedModel = resourcefulRelationshipDefinition.relatedModel()
    if (!isResourcefulModel(relatedModel)) {
      throw new E_RELATIONSHIP_NOT_FOUND_EXCEPTION()
    }
    const { valid: selections, isFullSet } = await sanitizeSelect(query, relatedModel, ctx, app)
    const { filter, page, perPage, fields, sort, hooks, showCountMeta } =
      this.#parseResourcefulIndexArguments(query, selections, resource, relatedModel)
    const response = await model.$onResourcefulReadRelationship(
      resolvedId as any,
      relationship,
      filter,
      page,
      perPage,
      fields,
      sort,
      ctx,
      app,
      hooks
    )
    if (isCountRequest) {
      return respond({
        status: 200,
        body: response.total,
        metadata: {
          context: '', // required for shape of ODataMetadata but not used in $count responses
        },
        raw: true,
      })
    }
    const ret: ResourcefulIndexResponse = {
      records: response.records,
      total: response.total,
      page: response.page,
      perPage: response.perPage,
      aggregations: response.aggregations,
    }
    const runner = this.#mutators.runner('readRelated')
    await runner.run(ret, ctx)
    const metadata: ODataMetadata = stripUndefinedValuesFromObject({
      context: await this.getODataContextUrl(
        `Collection(Default.${relatedModel.$resourcefulName})${this.#makeSelectionSubsetForContextUrl(selections, isFullSet, relatedModel)}`
      ),
      count: showCountMeta && !isCountRequest ? ret.total : undefined,
      nextLink: this.#computeODataNextLink(ret.page, ret.perPage, ret.total, query, ctx),
    })
    return respond({
      status: 200,
      body: santizeResponsePayload(ret.records, selections, preference),
      metadata,
      model: relatedModel,
      isArray: true,
    })
  }

  #parseRelationshipFromSyncRelatedResourceFragment(resource: string, model: ResourcefulModel) {
    const startOfActionName = resource.lastIndexOf('/Default.Sync')
    if (startOfActionName === -1) {
      return undefined
    }
    const resourceAction = resource.slice(startOfActionName + 1)
    const toStrip = `Default.Sync${string.pascalCase([this.#subpath, 'related'].join(' '))}`
    let relationshipName = resourceAction.replace(toStrip, '')
    if (relationshipName.startsWith('/')) {
      relationshipName = relationshipName.slice(1)
    }
    if (relationshipName.endsWith('/$ref')) {
      relationshipName = relationshipName.slice(0, -5)
    }
    if (relationshipName.endsWith('/')) {
      relationshipName = relationshipName.slice(0, -1)
    }
    if (!relationshipName) {
      return undefined
    }
    const candidates = [relationshipName, string.camelCase(relationshipName)]
    for (const candidate of candidates) {
      const resourcefulRelationshipDefinition = model.$resourcefulRelationships.get(candidate)
      if (!resourcefulRelationshipDefinition) {
        continue
      }
      const lucidRelationshipDefinition = model.$getRelation(candidate)
      if (!lucidRelationshipDefinition) {
        continue
      }
      const relatedModel = resourcefulRelationshipDefinition.relatedModel()
      if (!isResourcefulModel(relatedModel)) {
        continue
      }
      return {
        relationship: candidate,
        relatedModel,
        resourcefulRelationshipDefinition,
        lucidRelationshipDefinition,
      }
    }
    return undefined
  }

  async #serveModelSyncRelated(ctx: HttpContext) {
    const { resource, model, respond } = await this.resolveRequestRequirements(ctx)
    const uid = this.#parseInstanceUidFromResourceFragment(resource)
    if (!uid) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    if (!uid || !/^-?\d+$/.test(uid)) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const id = Number(uid)
    if (!Number.isSafeInteger(id)) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const relInfo = this.#parseRelationshipFromSyncRelatedResourceFragment(resource, model)
    if (!isObject(relInfo)) {
      throw new E_RELATIONSHIP_NOT_FOUND_EXCEPTION()
    }
    const { relationship, lucidRelationshipDefinition, relatedModel } = relInfo
    switch (lucidRelationshipDefinition.type) {
      case 'hasOne':
      case 'belongsTo':
        throw new E_RELATIONSHIP_NOT_FOUND_EXCEPTION()
      case 'hasManyThrough':
        throw new E_UNSYNCABLE_RELATIONSHIP_EXCEPTION()
    }
    const { error, value } = modelRelationshipSyncPayloadSchema.validate(ctx.request.all(), {
      abortEarly: false,
      convert: true,
    })
    if (error) {
      throw normalizeError(error)
    }
    const instance = await model.query().where(model.primaryKey, id).first()
    if (!instance) {
      throw new E_RECORD_NOT_FOUND_EXCEPTION()
    }
    const idsToInclude = value.ruids.map((ruid) => Number(ruid)) as number[]
    if (lucidRelationshipDefinition.type === 'manyToMany') {
      try {
        // @ts-ignore
        await instance.related(relationship).sync(idsToInclude)
      } catch (e) {
        throw normalizeError(e)
      }
    }
    if (lucidRelationshipDefinition.type === 'hasMany') {
      // in order to ensure that model hooks are run, we will load the relevant related models which need to be synced
      // and then update them one by one
      const foreignKey = (lucidRelationshipDefinition as any).options.foreignKey
      if (!foreignKey) {
        throw new E_INVALID_RELATIONSHIP_EXCEPTION(
          `HasMany relationship '${relationship}' missing foreignKey`
        )
      }
      const [relatedModelInstancesEnclude, relatedModelInstancesToInclude] = await Promise.all([
        // @ts-ignore
        instance.related(relationship).query().whereNotIn(relatedModel.primaryKey, idsToInclude),
        // @ts-ignore
        relatedModel.query().whereIn(relatedModel.primaryKey, idsToInclude),
      ])
      const relatedModelInstancesEncludeResults = await pMap(
        relatedModelInstancesEnclude as InstanceType<typeof relatedModel>[],
        async (relatedInstance) => {
          relatedInstance.merge({
            [foreignKey]: null,
          })
          try {
            await relatedInstance.save()
          } catch (e) {
            return e
          }
        },
        { concurrency: 1, stopOnError: false }
      )
      const relatedModelInstancesEncludeResultsErr = relatedModelInstancesEncludeResults.filter(
        (result) => 'undefined' !== typeof result
      )
      if (relatedModelInstancesEncludeResultsErr.length > 0) {
        throw relatedModelInstancesEncludeResultsErr[0]
      }
      const relatedModelInstancesToIncludeResults = await pMap(
        relatedModelInstancesToInclude as InstanceType<typeof relatedModel>[],
        async (relatedInstance) => {
          relatedInstance.merge({
            [foreignKey]: instance.$getAttribute(model.primaryKey),
          })
          try {
            await relatedInstance.save()
          } catch (e) {
            return e
          }
        },
        { concurrency: 1, stopOnError: false }
      )
      const relatedModelInstancesToIncludeResultsErr = relatedModelInstancesToIncludeResults.filter(
        (result) => 'undefined' !== typeof result
      )
      if (relatedModelInstancesToIncludeResultsErr.length > 0) {
        throw relatedModelInstancesToIncludeResultsErr[0]
      }
    }
    return respond({
      status: 204,
      body: undefined,
      metadata: {
        context: '', // required for shape of ODataMetadata but not used in sync responses
      },
    })
  }

  async #cacheOperations() {
    if (
      this.#operations.size > 0 ||
      !isObject(this.#options.additional) ||
      Object.keys(this.#options.additional).length === 0
    )
      return
    const model = await this.#model
    if (model instanceof E_UNRESOLVABLE_MODEL) {
      return
    }
    if (isObject(this.#options.additional)) {
      Object.entries(this.#options.additional).forEach(([methodAndPath, callback]) => {
        const [method, path] = methodAndPath.split('|')
        if (!method || !path) return
        const opName = makeOpName(method, model.$resourcefulName, path)
        this.#operations.set(opName, callback)
      })
    }
  }

  #parseOperationNameFromResourceFragment(resource: string) {
    const pattern = /\/Default\.([^.\/(]+)/
    const match = resource.match(pattern)
    if (match && match[1] !== undefined) {
      return match[1]
    }
    return undefined
  }

  #getMutatedContext(ctx: HttpContext, params: Record<string, any>) {
    const mutated: HttpContext = Object.create(ctx)
    mutated.params = Object.freeze(params)
    return Object.freeze(mutated)
  }

  async #processOperation(callback: RouteCallback, ctx: HttpContext) {
    let next: ((ctx: HttpContext) => unknown | PromiseLike<unknown> | Promise<unknown>) | undefined
    if (typeof callback === 'function') {
      next = async (c) => await callback(c)
    } else if (Array.isArray(callback) && callback.length === 2) {
      const [maybeResolvableController, method] = callback
      if (
        method in maybeResolvableController &&
        typeof maybeResolvableController[method] === 'function'
      ) {
        // @ts-ignore
        next = async (c) => await maybeResolvableController[method](c)
      } else {
        // @ts-ignore
        const resolvedController = maybeResolvableController()
        if (typeof resolvedController[method] === 'function') {
          next = async (c) => await resolvedController[method](c)
        }
      }
    }
    if (!next) return
    await next(ctx)
  }

  async #processCallbackOrAdditionalResourcefulRouteDetails(
    callback: RouteCallback | AdditionalResourcefulRouteDetails,
    ctx: HttpContext
  ) {
    if (isAdditionalResourcefulRouteDetails(callback)) {
      await this.#processOperation(callback.handler, ctx)
    } else {
      await this.#processOperation(callback, ctx)
    }
  }

  async #serveModelFunction(ctx: HttpContext) {
    const { resource } = await this.resolveRequestRequirements(ctx)
    await this.#cacheOperations()
    const opName = this.#parseOperationNameFromResourceFragment(resource)
    if (!opName) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const callback = this.#operations.get(opName)
    if ('undefined' === typeof callback) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const mutatedCtx = this.#getMutatedContext(ctx, {})
    await this.#processCallbackOrAdditionalResourcefulRouteDetails(callback, mutatedCtx)
  }

  async #serveModelAction(ctx: HttpContext) {
    const { resource } = await this.resolveRequestRequirements(ctx)
    await this.#cacheOperations()
    const opName = this.#parseOperationNameFromResourceFragment(resource)
    if (!opName) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const callback = this.#operations.get(opName)
    if ('undefined' === typeof callback) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const mutatedCtx = this.#getMutatedContext(ctx, {})
    await this.#processCallbackOrAdditionalResourcefulRouteDetails(callback, mutatedCtx)
  }

  async #serveModelInstanceFunction(ctx: HttpContext) {
    const { resource } = await this.resolveRequestRequirements(ctx)
    await this.#cacheOperations()
    const opName = this.#parseOperationNameFromResourceFragment(resource)
    if (!opName) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const callback = this.#operations.get(opName)
    if ('undefined' === typeof callback) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const uid = this.#parseInstanceUidFromResourceFragment(resource)
    if (!uid) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    if (!uid || !/^-?\d+$/.test(uid)) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const id = Number(uid)
    if (!Number.isSafeInteger(id)) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const mutatedCtx = this.#getMutatedContext(ctx, { uid: id })
    await this.#processCallbackOrAdditionalResourcefulRouteDetails(callback, mutatedCtx)
  }

  async #serveModelInstanceAction(ctx: HttpContext) {
    const { resource } = await this.resolveRequestRequirements(ctx)
    await this.#cacheOperations()
    const opName = this.#parseOperationNameFromResourceFragment(resource)
    if (!opName) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const callback = this.#operations.get(opName)
    if ('undefined' === typeof callback) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const uid = this.#parseInstanceUidFromResourceFragment(resource)
    if (!uid) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    if (!uid || !/^-?\d+$/.test(uid)) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const id = Number(uid)
    if (!Number.isSafeInteger(id)) {
      throw new E_ROUTE_NOT_FOUND([ctx.request.method(), ctx.request.url()])
    }
    const mutatedCtx = this.#getMutatedContext(ctx, { uid: id })
    await this.#processCallbackOrAdditionalResourcefulRouteDetails(callback, mutatedCtx)
  }

  #getPageAndPerPageFromTopAndSkip(query: Record<string, any>, isCountRequest: boolean) {
    if (isCountRequest) {
      return { page: 1, perPage: 1 }
    }
    const rawTop = isStringContent(query['$top']) ? query['$top'].trim() : '10'
    const rawSkip = isStringContent(query['$skip']) ? query['$skip'].trim() : '0'
    const parsedTop = Number.parseInt(rawTop, 10)
    const parsedSkip = Number.parseInt(rawSkip, 10)
    const top = Number.isSafeInteger(parsedTop) && parsedTop > 0 ? parsedTop : 10
    const skip = Number.isSafeInteger(parsedSkip) && parsedSkip >= 0 ? parsedSkip : 0
    const page = Math.floor(skip / top) + 1
    const perPage = top > 100 ? 100 : top
    return { page, perPage }
  }

  #getSortFromOrderBy($order: string | undefined) {
    const ret: [string, 'asc' | 'desc'][] = []
    if ('undefined' === typeof $order || !isStringContent($order)) {
      return ret
    }
    const fragments = $order.split(',')
    fragments.forEach((fragment) => {
      const [field, dir] = fragment
        .trim()
        .split(/\s+/)
        .map((s) => s.trim()) as [string, string | undefined]
      ret.push([field, dir === 'desc' ? 'desc' : 'asc'])
    })
    return ret
  }

  #getDialectFromModel(model: ResourcefulModel): Dialect {
    const adapter = model.$adapter
    const client = adapter.modelConstructorClient(model)
    const clientDialect = client.dialect.name
    switch (clientDialect) {
      case 'postgres':
      case 'redshift':
        return 'postgres'
      case 'mysql':
        return 'mysql'
      case 'sqlite3':
      case 'better-sqlite3':
        return 'sqlite'
      case 'mssql':
      case 'oracledb':
      case 'libsql':
      default:
        throw new E_UNSUPPORTED_DATABASE_DIALECT(clientDialect || 'unknown')
    }
  }

  #parseResourcefulIndexArguments(
    query: Record<string, any>,
    selections: string[] | undefined,
    resource: string,
    model: ResourcefulModel
  ) {
    console.log('ResourcefulModelController##parseResourcefulIndexArguments')
    const isCountRequest = resource.endsWith('/$count')
    const showCountMeta = '$count' in query && query['$count'] === 'true'
    if (isStringContent(query['$search'])) {
      throw new Exception('$search is not implemented for this OData endpoint', {
        status: 501,
        code: 'E_ODATA_SEARCH_NOT_IMPLEMENTED',
      })
    }

    const filter: string | undefined = undefined
    const { page, perPage } = this.#getPageAndPerPageFromTopAndSkip(query, isCountRequest)
    const sort = this.#getSortFromOrderBy(query['$orderby'])
    const aliases: Map<string, string> = new Map()
    const keyToColumn: Map<string, string> = new Map()
    Object.entries(query).forEach(([k, v]) => {
      if (isStringContent(k) && k.startsWith('@')) {
        aliases.set(k, isStringContent(v) ? v.trim() : '')
      }
    })
    Object.entries(model.$keys.serializedToColumns.all()).forEach(([key, col]) => {
      keyToColumn.set(key, col)
    })
    const dialect = this.#getDialectFromModel(model)
    const $filter = isStringContent(query['$filter']) ? query['$filter'].trim() : ''
    const hooks = [
      ...this.#scopeRestrictors,
      ODataToLucidTranslator.toHook($filter, aliases, keyToColumn, dialect),
    ]
    return {
      filter,
      page,
      perPage,
      fields: selections,
      sort,
      hooks,
      showCountMeta,
      isCountRequest,
    }
  }

  #computeODataNextLink(
    currentPage: number,
    perPage: number,
    totalItems: number,
    query: Record<string, any>,
    ctx: HttpContext
  ): string | undefined {
    const possiblyServedCount = currentPage * perPage
    const servedCount = possiblyServedCount > totalItems ? totalItems : possiblyServedCount
    if (servedCount >= totalItems) {
      return undefined
    }
    const updatedQuery = { ...query }
    updatedQuery['$top'] = String(perPage)
    updatedQuery['$skip'] = String(servedCount)
    const absUrl = ctx.request.completeUrl(false)
    return [absUrl, qs.stringify(updatedQuery, { addQueryPrefix: true })].join('')
  }

  async #serveModelIndex(ctx: HttpContext) {
    console.log('ResourcefulModelController##serveModelIndex')
    const { model, app, respond, selections, isFullSet, preference, query, resource } =
      await this.resolveRequestRequirements(ctx)

    // Avoid passing relationship names into the underlying index fields selection.
    const relationshipNames = new Set(
      Array.from(model.$resourcefulRelationships.keys()).map((k) => k.toLowerCase())
    )
    const safeSelections = Array.isArray(selections)
      ? selections.filter((k) => !relationshipNames.has(String(k).toLowerCase()))
      : selections

    const { filter, page, perPage, fields, sort, hooks, showCountMeta, isCountRequest } =
      this.#parseResourcefulIndexArguments(query, safeSelections, resource, model)
    const response = await model.$onResourcefulIndex(
      filter,
      page,
      perPage,
      fields,
      sort,
      ctx,
      app,
      hooks
    )

    console.log(`ResourcefulModelController#serveModelIndex - Response:`)
    console.log(response)

    if (isCountRequest) {
      return respond({
        status: 200,
        body: response.total,
        metadata: {
          context: '', // required for shape of ODataMetadata but not used in $count responses
        },
        raw: true,
      })
    }
    const ret: ResourcefulIndexResponse = {
      records: response.records,
      total: response.total,
      page: response.page,
      perPage: response.perPage,
      aggregations: response.aggregations,
    }
    const runner = this.#mutators.runner('index')
    await runner.run(ret, ctx)

    // Handle $expand for index responses by loading related records per returned entity.
    // NOTE: This issues one DB query per (expansion × record). To prevent runaway load,
    // hydration is capped at ODATA_EXPAND_MAX_RECORDS (default 100). If the result set
    // exceeds this cap and $expand is requested, only the first N records are returned
    // and metadata (count, nextLink) is adjusted to reflect the truncated page.
    const ODATA_EXPAND_MAX_RECORDS = 100
    const expansions = sanitizeExpand(query)
    let expandTruncated = false
    if (Array.isArray(expansions) && expansions.length > 0) {
      // Truncate records to the expand cap to guarantee consistent shape.
      if (ret.records.length > ODATA_EXPAND_MAX_RECORDS) {
        ret.records = ret.records.slice(0, ODATA_EXPAND_MAX_RECORDS)
        expandTruncated = true
      }
      const relationshipMap = new Map(
        Array.from(model.$resourcefulRelationships.keys()).map((k) => [k.toLowerCase(), k])
      )
      const resolved: string[] = []
      for (const e of expansions) {
        const r = relationshipMap.get(String(e).toLowerCase())
        if (r) resolved.push(r)
      }
      if (resolved.length > 0) {
        for (const originalExp of expansions) {
          const rel = relationshipMap.get(String(originalExp).toLowerCase())
          if (!rel) continue
          for (const record of ret.records) {
            try {
              const relResp = await model.$onResourcefulReadRelationship(
                Number(record[model.primaryKey]),
                rel,
                undefined,
                1,
                1000,
                undefined,
                undefined,
                ctx,
                app,
                []
              )
              // Attach under the original expansion token (preserve requested casing).
              record[originalExp] = Array.isArray(relResp && (relResp as any).records)
                ? (relResp as any).records
                : []
            } catch (e) {
              record[originalExp] = []
            }
          }
        }
      }
    }

    // When $expand truncated the result set, compute a nextLink so the client
    // knows to fetch the remaining records. Use the truncated count as the
    // effective page size for metadata purposes.
    const effectivePerPage = expandTruncated ? ODATA_EXPAND_MAX_RECORDS : ret.perPage
    const effectivePage = expandTruncated ? 1 : ret.page
    const metadata: ODataMetadata = stripUndefinedValuesFromObject({
      context: await this.getODataContextUrl(
        `${this.#subpath}${this.#makeSelectionSubsetForContextUrl(selections, isFullSet, model)}`
      ),
      count: showCountMeta && !isCountRequest ? ret.total : undefined,
      nextLink: this.#computeODataNextLink(effectivePage, effectivePerPage, ret.total, query, ctx),
    })
    return respond({
      status: 200,
      body: santizeResponsePayload(ret.records, selections, preference),
      metadata,
      model,
      isArray: true,
    })
  }

  async #applyMiddlewares(ctx: HttpContext, next: (ctx: HttpContext) => Promise<void>) {
    const middleware = new Middleware<MiddlewareFn>()
    if (this.#options.middleware) {
      if (Array.isArray(this.#options.middleware)) {
        this.#options.middleware.forEach((m) => middleware.add(m))
      } else if (isObject(this.#options.middleware) && 'handle' in this.#options.middleware) {
        middleware.add(this.#options.middleware.handle as MiddlewareFn)
      } else if (typeof this.#options.middleware === 'function') {
        middleware.add(this.#options.middleware as MiddlewareFn)
      }
    }
    middleware.add(async (c) => {
      await next(c)
    })
    return await middleware.runner().run(async (fn, nxt) => fn(ctx, nxt))
  }

  async serveModelRead(ctx: HttpContext) {
    return await this.#applyMiddlewares(ctx, this.#serveModelRead.bind(this, ctx))
  }
  async serveModelUpdate(ctx: HttpContext) {
    return await this.#applyMiddlewares(ctx, this.#serveModelUpdate.bind(this, ctx))
  }
  async serveModelDelete(ctx: HttpContext) {
    return await this.#applyMiddlewares(ctx, this.#serveModelDelete.bind(this, ctx))
  }
  async serveModelCreate(ctx: HttpContext) {
    return await this.#applyMiddlewares(ctx, this.#serveModelCreate.bind(this, ctx))
  }
  async serveModelReadRelated(ctx: HttpContext) {
    return await this.#applyMiddlewares(ctx, this.#serveModelReadRelated.bind(this, ctx))
  }
  async serveModelSyncRelated(ctx: HttpContext) {
    return await this.#applyMiddlewares(ctx, this.#serveModelSyncRelated.bind(this, ctx))
  }
  async serveModelFunction(ctx: HttpContext) {
    return await this.#applyMiddlewares(ctx, this.#serveModelFunction.bind(this, ctx))
  }
  async serveModelAction(ctx: HttpContext) {
    return await this.#applyMiddlewares(ctx, this.#serveModelAction.bind(this, ctx))
  }
  async serveModelInstanceFunction(ctx: HttpContext) {
    return await this.#applyMiddlewares(ctx, this.#serveModelInstanceFunction.bind(this, ctx))
  }
  async serveModelInstanceAction(ctx: HttpContext) {
    return await this.#applyMiddlewares(ctx, this.#serveModelInstanceAction.bind(this, ctx))
  }
  async serveModelIndex(ctx: HttpContext) {
    return await this.#applyMiddlewares(ctx, this.#serveModelIndex.bind(this, ctx))
  }
}
